<script lang="ts">
    import dayjs from 'dayjs';
    import { parseDate } from '@internationalized/date';

    import {
        DefaultOptionList,
        DefaultOptions,
    } from '$saga/types/search.types';
    import FilterBadges from '$saga/views/Search/Filters/FilterBadges.svelte';
    import {
        findCurrentSelectedFilterLabel,
        initializeLabelValues,
    } from '$saga/views/Search/utils';
    import MainFilters from '$saga/views/Search/MainFilters/MainFilters.svelte';
    import { filtersContext } from '$saga/views/Search/store';
    import { SearchForm, Sorting } from './types';

    export let defaultOptions: DefaultOptionList;
    export let defaultDestinationOptions: DefaultOptions;
    export let defaultSportOptions: DefaultOptions;
    export let searchForm: SearchForm | undefined = undefined;
    export let isHomePage: boolean = false;

    $filtersContext.formValues = {
        destination: searchForm?.destinations ?? [],
        theme: searchForm?.sports ?? [],
        startDate: searchForm?.startDate?.date
            ? parseDate(dayjs(searchForm.startDate.date).format('YYYY-MM-DD'))
            : undefined,
        endDate: searchForm?.endDate?.date
            ? dayjs(searchForm.endDate.date).toString()
            : undefined,
        flexibility: searchForm?.flexibility ?? 0,
        nbPax: searchForm?.nbPax?.toString() ?? '0',
        // adults: searchForm?.nbPax?.toString() ?? '0',
        // children: '0',
        // childrenAges: [],
        // secondary
        levels: searchForm?.levels ?? [],
        types: searchForm?.types ?? [],
        durationMin: searchForm?.durationMin ?? defaultOptions.durationMin,
        durationMax: searchForm?.durationMax ?? defaultOptions.durationMax,
        budgetMin: searchForm?.budgetMin ?? defaultOptions.budgetMin,
        budgetMax: searchForm?.budgetMax ?? defaultOptions.budgetMax,
        tags: searchForm?.tags ?? [],
    };

    $filtersContext.formLabels = initializeLabelValues(
        $filtersContext.formValues,
        defaultOptions,
        defaultDestinationOptions,
        defaultSportOptions,
    );
    $filtersContext.defaultOptions = defaultOptions;
    $filtersContext.defaultSportOptions = defaultSportOptions;
    $filtersContext.defaultDestinationOptions = defaultDestinationOptions;
    $filtersContext.isHomePage = isHomePage;
    $filtersContext.isSubmitting = false;

    const handleFormSubmissionAnalytics = (
        filterName: string | null = null,
    ) => {
        if (!('dataLayer' in window)) {
            return;
        }
        // Récupérer les valeurs des champs cachés
        let destinationLabels =
            $filtersContext.formValues.destination.map((destination) =>
                findCurrentSelectedFilterLabel(
                    destination,
                    defaultDestinationOptions,
                ),
            ) ?? 'without_search';
        let levelLabel =
            $filtersContext.formValues.levels.map((level) =>
                findCurrentSelectedFilterLabel(level, defaultOptions.levels),
            ) ?? 'without_search';
        let sportLabels =
            $filtersContext.formValues.theme.map((theme) =>
                findCurrentSelectedFilterLabel(theme, defaultSportOptions),
            ) ?? 'without_search';
        let monthLabel = 'without_search';
        let tagLabel =
            $filtersContext.formValues.tags.map((tag) =>
                findCurrentSelectedFilterLabel(tag, defaultOptions.tags),
            ) ?? 'without_search';
        let typeLabel =
            $filtersContext.formValues.types.map((type) =>
                findCurrentSelectedFilterLabel(type, defaultOptions.types),
            ) ?? 'without_search';
        let departureDate = 'without_search';
        let travelers = 'without_search';

        // TODO send multiple filter values (secondary filters values)
        if (filterName) {
            let filterValue = null;
            if (filterName === 'themes') {
                filterValue = tagLabel;
            }
            if (filterName === 'levels') {
                filterValue = levelLabel;
            }
            if (filterName === 'types') {
                filterValue = typeLabel;
            }
            if (filterName === 'month') {
                filterValue = monthLabel;
            }
            if (filterName === 'duration') {
                filterValue = `${$filtersContext.formValues.durationMin} - ${$filtersContext.formValues.durationMax} jours`;
            }
            if (filterName === 'budget') {
                filterValue = `${$filtersContext.formValues.budgetMin} - ${$filtersContext.formValues.budgetMax} €`;
            }
            window.dataLayer.push({
                event: 'santianoEcommerce',
                eventCommand: 'filter',
                filterName: filterName,
                filterValue: filterValue,
            });
        } else {
            departureDate =
                $filtersContext.formValues.startDate?.toString() ??
                'without_search';
            travelers =
                $filtersContext.formValues.nbPax?.toString() ??
                'without_search';

            if (travelers === '0') {
                travelers = 'without_search';
            }
            window.dataLayer.push({
                event: 'santianoEcommerce',
                eventCommand: 'search',
                destinations: destinationLabels,
                sports: sportLabels,
                departureDate: departureDate,
                travelers: travelers,
            });
        }
    };
</script>

<MainFilters {handleFormSubmissionAnalytics} />

{#if !isHomePage && searchForm}
    <div
        class="mx-auto my-m flex max-w-layout-max items-center justify-between px-l desktop:px-5xl"
    >
        <FilterBadges {searchForm} />
        <!-- sorting -->

        <div class="flex items-center gap-xs">
            <span class="text-sm desktop:text-xs"> Trier par : </span>
            <select
                name="sort"
                form="travel-search-engine-form"
                data-controller="search-result-sort"
                class="mx-auto min-h-2xl cursor-pointer self-center border-none bg-transparent bg-[98%] py-xs pl-m pr-xl text-sm text-content-brand shadow-[inset_0_0_0_0_transparent] focus:bg-transparent focus:bg-[98%] desktop:mx-0 desktop:text-xs"
            >
                <option
                    value={Sorting.DEFAULT}
                    selected={searchForm.sort === Sorting.DEFAULT}
                >
                    Recommandation
                </option>
                <option
                    value="price_asc"
                    selected={searchForm.sort === Sorting.PRICE_ASC}
                >
                    Prix croissant
                </option>
                <option
                    value={Sorting.PRICE_DESC}
                    selected={searchForm.sort === Sorting.PRICE_DESC}
                >
                    Prix décroissant
                </option>
                <option
                    value={Sorting.DURATION_DESC}
                    selected={searchForm.sort === Sorting.DURATION_DESC}
                >
                    Durée du plus long au plus court
                </option>
                <option
                    value={Sorting.DURATION_ASC}
                    selected={searchForm.sort === Sorting.DURATION_ASC}
                >
                    Durée du plus court au plus long
                </option>
                <option
                    value={Sorting.REVIEWS_DESC}
                    selected={searchForm.sort === Sorting.REVIEWS_DESC}
                >
                    Avis
                </option>
                <option
                    value={Sorting.DEPARTURE_DATE}
                    selected={searchForm.sort === Sorting.DEPARTURE_DATE}
                >
                    Date de départ
                </option>
            </select>
        </div>
    </div>
{/if}
<!-- main form filters, hidden -->
<!--Desti -->
{#each $filtersContext.formValues.destination as value}
    <input
        aria-hidden="true"
        tabindex="-1"
        class="hidden"
        name="destination[]"
        {value}
    />
{/each}

<!--Sport -->
{#each $filtersContext.formValues.theme as value}
    <input
        aria-hidden="true"
        tabindex="-1"
        class="hidden"
        name="theme[]"
        {value}
    />
{/each}

<!--    startDate -->
<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="startDate"
    value={$filtersContext.formValues.startDate
        ? dayjs($filtersContext.formValues.startDate.toString()).format(
              'DD/MM/YYYY',
          )
        : ''}
/>
<!--    endDate -->
<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="endDate"
    value={$filtersContext.formValues.endDate
        ? dayjs($filtersContext.formValues.endDate.toString()).format(
              'DD/MM/YYYY',
          )
        : ''}
/>

<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="flexibility"
    value={$filtersContext.formValues.flexibility}
/>

<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="nbPax"
    value={Number($filtersContext.formValues.nbPax)}
/>

<!-- Secondary filters inputs -->
{#each $filtersContext.formValues.levels as level}
    <input
        aria-hidden="true"
        tabindex="-1"
        class="hidden"
        name="level[]"
        value={level}
    />
{/each}

{#each $filtersContext.formValues.types as type}
    <input
        aria-hidden="true"
        tabindex="-1"
        class="hidden"
        name="type[]"
        value={type}
    />
{/each}

<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="durationMin"
    value={$filtersContext.formValues.durationMin}
/>

<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="durationMax"
    value={$filtersContext.formValues.durationMax}
/>
<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="budgetMin"
    value={$filtersContext.formValues.budgetMin}
/>
<input
    aria-hidden="true"
    tabindex="-1"
    class="hidden"
    name="budgetMax"
    value={$filtersContext.formValues.budgetMax}
/>

{#each $filtersContext.formValues.tags as tag}
    <input
        aria-hidden="true"
        tabindex="-1"
        class="hidden"
        name="tag[]"
        value={tag}
    />
{/each}
